<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
                <v-expansion-panel>
                  <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                    <v-icon class="me-2">mdi-cog</v-icon>
                    Process Configuration
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-form @submit.prevent="handleSubmit">
                      <v-row class="py-3 px-3" align="end" dense>
                        <!-- Process Date -->
                        <v-col cols="12" md="3">
                          <v-date-input
                            input-format="dd/mm/yyyy"
                            v-model="formData.process_date"
                            label="Process Date *"
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            prepend-icon=""
                            prepend-inner-icon="mdi-calendar"
                            hide-details="auto"
                            required
                          />
                        </v-col>

                        <!-- Year -->
                        <v-col cols="12" md="3">
                          <v-select
                            v-model="formData.year"
                            :items="yearOptions"
                            label="Year *"
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            prepend-inner-icon="mdi-calendar-year"
                            hide-details="auto"
                            required
                          />
                        </v-col>

                        <!-- Month -->
                        <v-col cols="12" md="3">
                          <v-select
                            v-model="formData.month"
                            :items="months"
                            item-title="label"
                            item-value="value"
                            label="Month *"
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            prepend-inner-icon="mdi-calendar-month"
                            hide-details="auto"
                            required
                          />
                        </v-col>

                        <!-- Process Scope -->
                        <v-col cols="12" md="3">
                          <v-select
                            v-model="formData.process_scope"
                            :items="processScopeOptions"
                            item-title="label"
                            item-value="value"
                            label="Process Scope *"
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            prepend-inner-icon="mdi-filter"
                            hide-details="auto"
                            required
                            @update:model-value="handleScopeChange"
                          />
                        </v-col>

                        <!-- Department (show only when department is selected) -->
                        <v-col v-if="formData.process_scope === 'department'" cols="12" md="3">
                          <v-autocomplete
                            v-model="formData.department_id"
                            :items="departments"
                            :item-title="(item) => item.name || item.department_name || ''"
                            item-value="id"
                            label="Department *"
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            prepend-inner-icon="mdi-office-building"
                            clearable
                            hide-details="auto"
                            :loading="departmentsLoading"
                            placeholder="Select department"
                            required
                          />
                        </v-col>

                        <!-- Employee (show only when employee is selected) -->
                        <v-col v-if="formData.process_scope === 'employee'" cols="12" md="3">
                          <v-autocomplete
                            v-model="formData.employee_id"
                            :items="employees"
                            :item-title="(item) => formatEmployeeLabel(item)"
                            item-value="id"
                            label="Employee *"
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            prepend-inner-icon="mdi-account"
                            clearable
                            hide-details="auto"
                            :loading="employeesLoading"
                            placeholder="Search employee"
                            required
                          />
                        </v-col>

                      </v-row>


                  <v-card-actions class="justify-end mt-4 pa-4">
                    <v-btn
                      color="grey"
                      variant="flat"
                      type="button"
                      size="large"
                      @click="submitForm(0)"
                      :loading="loading"
                    >
                      Draft
                    </v-btn>
                    <v-btn
                      color="primary"
                      variant="flat"
                      type="button"
                      size="large"
                      @click="submitForm(1)"
                      :loading="loading"
                    >
                      POST
                    </v-btn>
                  </v-card-actions>


                    </v-form>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'

const router = useRouter()
const store = useStore()
const loading = ref(false)
const panel = ref([0])
const departmentsLoading = ref(false)
const employeesLoading = ref(false)

// Form data
const formData = reactive({
  process_date: '',
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  process_scope: 'all',
  department_id: null,
  employee_id: null
})

// Options
const months = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 }
]

// Generate year options (from 2020 to current year + 5)
const currentYear = new Date().getFullYear()
const yearOptions = computed(() => {
  const years = []
  for (let year = 2020; year <= currentYear + 5; year++) {
    years.push(year)
  }
  return years.reverse() // Show most recent years first
})

const processScopeOptions = [
  { label: 'All', value: 'all' },
  { label: 'By Department', value: 'department' },
  { label: 'By Employee', value: 'employee' }
]

// Data
const departments = ref([])
const employees = ref([])

// Methods
const formatEmployeeLabel = (item) => {
  if (!item) return ''
  return `${item.name || ''}${item.employee_code ? ` - ${item.employee_code}` : ''}`
}

// Methods
const setDefaultDate = () => {
  const today = new Date()
  formData.process_date = today.toISOString().split('T')[0]
  formData.year = today.getFullYear()
  formData.month = today.getMonth() + 1
}

const handleScopeChange = () => {
  // Reset fields when switching scope
  if (formData.process_scope === 'department') {
    formData.employee_id = null
  } else if (formData.process_scope === 'employee') {
    formData.department_id = null
  } else if (formData.process_scope === 'all') {
    // Reset both fields when selecting 'all'
    formData.department_id = null
    formData.employee_id = null
  }
}

const loadDepartments = async () => {
  departmentsLoading.value = true
  try {
    const response = await store.dispatch('getDepartments')
    departments.value = response.data.data || []
  } catch (error) {
    departments.value = []
    toast.error(error.message || 'Failed to load departments')
  } finally {
    departmentsLoading.value = false
  }
}

const loadEmployees = async () => {
  employeesLoading.value = true
  try {
    const response = await store.dispatch('getEmployees')
    employees.value = response.data.data || []
  } catch (error) {
    employees.value = []
    toast.error(error.message || 'Failed to load employees')
  } finally {
    employeesLoading.value = false
  }
}

const validateForm = () => {
  if (!formData.process_date) {
    toast.error('Please select a process date')
    return false
  }

  if (!formData.year || formData.year < 2020 || formData.year > 2100) {
    toast.error('Please enter a valid year')
    return false
  }

  if (!formData.month || formData.month < 1 || formData.month > 12) {
    toast.error('Please select a valid month')
    return false
  }

  if (!formData.process_scope) {
    toast.error('Please select a process scope')
    return false
  }

  // When scope is 'department', department is required
  if (formData.process_scope === 'department' && !formData.department_id) {
    toast.error('Please select a department')
    return false
  }

  // When scope is 'employee', employee is required
  if (formData.process_scope === 'employee' && !formData.employee_id) {
    toast.error('Please select an employee')
    return false
  }

  // When scope is 'all', both fields are optional (can select one, both, or neither)

  return true
}

  const currentStatus = ref("");

  const submitForm = (status) => {
      currentStatus.value = status;
      handleSubmit();
  };

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  loading.value = true

  try {
    // Prepare request data
    // When scope is 'all' but filters are selected, determine the appropriate scope
    let finalScope = formData.process_scope
    let finalDepartmentId = null
    let finalEmployeeId = null

    if (formData.process_scope === 'all') {
      // When 'all' is selected, no department or employee filters
      finalScope = 'all'
    } else if (formData.process_scope === 'department') {
      finalDepartmentId = formData.department_id
    } else if (formData.process_scope === 'employee') {
      finalEmployeeId = formData.employee_id
    }

    const requestData = {
      process_date: formData.process_date,
      year: parseInt(formData.year),
      month: parseInt(formData.month),
      process_scope: finalScope,
      department_id: finalDepartmentId,
      employee_id: finalEmployeeId,
      status: currentStatus.value,
    }

    const response = await store.dispatch('salaryProcess/runSalaryProcess', requestData)

    if (response.success) {
      toast.success(response.message || 'Salary process completed successfully!')
      // Reset form
      setDefaultDate()
      formData.process_scope = 'all'
      formData.department_id = null
      formData.employee_id = null
      // Redirect to list page after successful process
      setTimeout(() => {
        router.push({ name: 'salary-process-list' })
      }, 1000) // Small delay to show success message
    } else {
      toast.error(response.message || 'Failed to run salary process')
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to run salary process'
    toast.error(errorMessage)
  } finally {
    loading.value = false
  }
}


// Lifecycle
onMounted(() => {
  setDefaultDate()
  loadDepartments()
  loadEmployees()
})
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

